import * as S from './AllInclusiveRatePlan.styles';
import useCarousel from '@stencil/hook-use-carousel/src/useCarousel';
import {defaultTheme} from '@stencil/build-theme';
import {useDispatch, useSelector} from 'react-redux';

import HappyAngleRightIcon from '@stencil/icons/src/HappyAngleRightIcon/HappyAngleRightIcon';
import HappyAngleLeftIcon from '@stencil/icons/src/HappyAngleLeftIcon/HappyAngleLeftIcon';

import AllInclusiveDetailed from '../AllInclusiveDetailed/AllInclusiveDetailed';

//Selectors
import {
  inclusiveRatePlanSelector,
  roomFiltersDropdownSelector as roomsFilterDataSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';

import Modal, {useModal} from '@stencil/hotels-modal-component';
import {detailsPagePushGaThunk} from '@stencil/hotels-ga/src';

import {gaConfig} from '@stencil/hotels-config';

//Omniture Imports
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';
import useOmnitureImpressions from '@stencil/omniture-analytics/src/hooks/useOmnitureImpressions';
import {useMemo} from 'react';
import {RatePlanFilter} from '@stencil/hotels-types/src/details-cg/types';

const {DetailsGAClickEventActions} = gaConfig;

interface AllInclusiveRatePlanProps {
  setFilters?: (filter: RatePlanFilter[]) => void;
}

const AllInclusiveRatePlan = ({setFilters = filter => {}}: AllInclusiveRatePlanProps) => {
  const {isModalOpen, openModal, closeModal} = useModal();
  const inclusiveRatePlanData = useSelector(inclusiveRatePlanSelector);
  const {title = '', desc = '', data = []} = inclusiveRatePlanData;
  const roomsFilterData = useSelector(roomsFilterDataSelector);

  const dispatch = useDispatch();

  const [omniWatcherRef] = useOmnitureImpressions(
    DetailsPageComponents.AllInclusiveRatePlan,
    omnitureConfig.pages.DetailsUnif,
    {
      allInclusiveRatePlan: `${DetailsPageComponents.AllInclusiveRatePlan}_shown`,
    },
  );

  const inclusiveFilter =
    roomsFilterData.length &&
    roomsFilterData.filter(data =>
      data.ratePlanFilterList.some(filterData => filterData?.code === 'ALL_INCLUSIVE'),
    );

  const {emblaRef, scrollPrevious, scrollNext, leftArrowIsActive, rightArrowIsActive} = useCarousel(
    {
      emblaOptions: {},
      shouldAutoplay: false,
    },
  );

  const openDetailedAndScroll = (id, name) => {
    openModal();
    setTimeout(() => {
      const ele = document.getElementById(id);
      ele &&
        ele.scrollIntoView({
          block: 'start',
          inline: 'start',
          behavior: 'smooth',
        });
    }, 0);
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.AllInclusiveRatePlan}_${detailsClick.allInclusiveRatePlan.inclusionItemClicked}`,
          type: omnitureConfig.ctaType.Card,
          componentName: DetailsPageComponents.AllInclusiveRatePlan,
          destination: '',
        },
      }),
    );
  };

  const onLearnMoreClick = () => {
    openModal();
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.AllInclusiveRatePlan}_${detailsClick.allInclusiveRatePlan.learnMoreClick}`,
          type: omnitureConfig.ctaType.Card,
          componentName: DetailsPageComponents.AllInclusiveRatePlan,
          destination: '',
        },
      }),
    );
  };

  const onBookInclusiveCtaClick = () => {
    closeModal();
    setFilters(prevFilters => {
      const alreadyPresent = prevFilters?.filter(
        filter => filter?.code === inclusiveFilter?.[0]?.ratePlanFilterList?.[0].code,
      );
      return alreadyPresent?.length
        ? prevFilters
        : [...prevFilters, inclusiveFilter?.[0]?.ratePlanFilterList?.[0]];
      // const alreadyPresent = prevFilters?.some(
      //   filter => filter.name === inclusiveFilter?.[0]?.name,
      // );
      // return alreadyPresent ? prevFilters : [...prevFilters, inclusiveFilter[0]];
    });

    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.AllInclusiveRatePlan}_${detailsClick.allInclusiveRatePlan.bookInclusiveCta}`,
          type: omnitureConfig.ctaType.Card,
          componentName: DetailsPageComponents.AllInclusiveRatePlan,
          destination: '',
        },
      }),
    );
  };

  if (!data.length) return null;

  return (
    <S.IncRateProp ref={omniWatcherRef}>
      <S.IncRatePropHeader>
        <S.IncRatePropHeaderLeft>
          {title && <p className="incRateProp__heading">{title}</p>}
          {desc && <p className="incRateProp__subHeading">{desc}</p>}
        </S.IncRatePropHeaderLeft>
        <a onClick={() => onLearnMoreClick()}>
          Learn More
          <span className="learnMoreArrow"></span>
        </a>
      </S.IncRatePropHeader>
      {leftArrowIsActive && (
        <S.SliderArrowLeft onClick={() => scrollPrevious()}>
          <HappyAngleLeftIcon fill={defaultTheme.colors.goBrandBlue.base} />
        </S.SliderArrowLeft>
      )}
      {data && data.length && (
        <S.IncRatePropSlider className="incRateProp__slider" ref={emblaRef}>
          <S.HomeCaroSliderInnerWrap
            snapLeftMargin
            listItemWidth="30%"
            marginBottom={defaultTheme.spacing.base}
          >
            {data.map(({imageUrl, text}, index) => (
              <li
                className="incRateProp__slider--item"
                key={`${text}-${index}`}
                onClick={() => openDetailedAndScroll(`inclusive-${index + 1}`, text)}
              >
                {imageUrl && (
                  <img src={imageUrl} alt={text} className="incRateProp__slider--itemImg" />
                )}
                {text && <p className="latoBlack font14 blackText">{text}</p>}
              </li>
            ))}
          </S.HomeCaroSliderInnerWrap>
        </S.IncRatePropSlider>
      )}
      {rightArrowIsActive && (
        <S.SliderArrowRight onClick={() => scrollNext()}>
          <HappyAngleRightIcon fill={defaultTheme.colors.goBrandBlue.base} />
        </S.SliderArrowRight>
      )}
      <Modal
        isOpen={isModalOpen}
        closeModal={closeModal}
        modalStyles={{
          width: '54rem',
        }}
      >
        <AllInclusiveDetailed onBookInclusiveCtaClick={onBookInclusiveCtaClick} />
      </Modal>
    </S.IncRateProp>
  );
};

export default AllInclusiveRatePlan;
